import { useCallback, useEffect, useState } from 'react'
import {
	fetchAcademicCalendar,
	putAcademicCalendar,
} from 'api/academicCalendar'
import { organizationContextStore } from 'shared/stores/organizationContextStore'

const emptyDraft = { startDate: '', endDate: '' }

const formatDate = value => {
	if (!value) return '—'
	return new Intl.DateTimeFormat('en', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		timeZone: 'UTC',
	}).format(new Date(`${value}T00:00:00Z`))
}

const getDurationInDays = ({ startDate, endDate }) => {
	if (!startDate || !endDate || startDate >= endDate) return 0
	const start = new Date(`${startDate}T00:00:00Z`)
	const end = new Date(`${endDate}T00:00:00Z`)
	return Math.round((end - start) / 86400000) + 1
}

const parseISOParts = iso => {
	if (!iso || typeof iso !== 'string') return null
	const parts = iso.split('-')
	if (parts.length !== 3) return null
	const year = Number(parts[0])
	const month = Number(parts[1])
	const day = Number(parts[2])
	if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
		return null
	}
	const d = new Date(Date.UTC(year, month - 1, day))
	if (
		d.getUTCFullYear() !== year ||
		d.getUTCMonth() !== month - 1 ||
		d.getUTCDate() !== day
	) {
		return null
	}
	return { year, month, day }
}

export const concreteDatesToMd = ({ startDate, endDate }) => {
	const start = parseISOParts(startDate)
	const end = parseISOParts(endDate)
	if (!start || !end) return null
	return {
		startMonth: start.month,
		startDay: start.day,
		endMonth: end.month,
		endDay: end.day,
	}
}

export const mdEquals = (a, b) =>
	Boolean(
		a &&
			b &&
			a.startMonth === b.startMonth &&
			a.startDay === b.startDay &&
			a.endMonth === b.endMonth &&
			a.endDay === b.endDay,
	)

const responseToDraft = data => ({
	startDate: data?.currentStartDate || '',
	endDate: data?.currentEndDate || '',
})

const responseToMd = data => ({
	startMonth: data.startMonth,
	startDay: data.startDay,
	endMonth: data.endMonth,
	endDay: data.endDay,
})

export const useSchoolScheduleOptions = ({ onCalendarUpdated } = {}) => {
	const [draft, setDraft] = useState(emptyDraft)
	const [persistedMd, setPersistedMd] = useState(null)
	const [label, setLabel] = useState(null)
	const [loadStatus, setLoadStatus] = useState('idle')
	const [loadError, setLoadError] = useState(null)
	const [saveStatus, setSaveStatus] = useState('idle')
	const [saveError, setSaveError] = useState(null)
	const [confirmOpen, setConfirmOpen] = useState(false)
	const [saved, setSaved] = useState(false)

	const draftMd = concreteDatesToMd(draft)
	const startParts = parseISOParts(draft.startDate)
	const endParts = parseISOParts(draft.endDate)
	const isValid = Boolean(
		startParts &&
			endParts &&
			draft.startDate < draft.endDate &&
			draftMd &&
			!(
				draftMd.startMonth === draftMd.endMonth &&
				draftMd.startDay === draftMd.endDay
			),
	)
	const hasChanges = Boolean(persistedMd && draftMd && !mdEquals(draftMd, persistedMd))
	const totalDays = getDurationInDays(draft)
	const dateRangeLabel = `${formatDate(draft.startDate)} – ${formatDate(draft.endDate)}`

	const applyResponse = useCallback(
		data => {
			setDraft(responseToDraft(data))
			setPersistedMd(responseToMd(data))
			setLabel(data?.label || null)
			onCalendarUpdated?.(data)
		},
		[onCalendarUpdated],
	)

	const loadCalendar = useCallback(async () => {
		setLoadStatus('loading')
		setLoadError(null)
		setSaved(false)
		setSaveError(null)
		setDraft(emptyDraft)
		setPersistedMd(null)
		try {
			await organizationContextStore.ensureInitialized()
			if (!organizationContextStore.selectedOrganizationId) {
				throw new Error('Organization context is required')
			}
			const response = await fetchAcademicCalendar()
			const data = response?.data ?? response
			applyResponse(data)
			setLoadStatus('success')
		} catch (error) {
			const message =
				error?.response?.data?.error ||
				error?.message ||
				'Failed to load academic calendar'
			setLoadStatus('error')
			setLoadError(message)
		}
	}, [applyResponse])

	useEffect(() => {
		loadCalendar()
	}, [loadCalendar])

	const updateDraft = (field, value) => {
		setDraft(current => ({ ...current, [field]: value }))
		setSaved(false)
		setSaveError(null)
	}

	const requestSave = event => {
		event.preventDefault()
		if (isValid && hasChanges && saveStatus !== 'saving') {
			setConfirmOpen(true)
		}
	}

	const closeConfirm = () => setConfirmOpen(false)

	const confirmSave = async () => {
		if (!isValid || !hasChanges || !draftMd) return
		setSaveStatus('saving')
		setSaveError(null)
		try {
			const response = await putAcademicCalendar(draftMd)
			const data = response?.data ?? response
			applyResponse(data)
			setConfirmOpen(false)
			setSaved(true)
			setSaveStatus('success')
		} catch (error) {
			const message =
				error?.response?.data?.error ||
				error?.message ||
				'Failed to save academic calendar'
			setSaveError(message)
			setSaveStatus('error')
			setConfirmOpen(false)
		}
	}

	return {
		draft,
		confirmOpen,
		saved,
		isValid,
		hasChanges,
		totalDays,
		dateRangeLabel,
		label,
		loadStatus,
		loadError,
		saveStatus,
		saveError,
		updateDraft,
		requestSave,
		closeConfirm,
		confirmSave,
		reload: loadCalendar,
	}
}

export default useSchoolScheduleOptions
